import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Checkbox,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  alpha,
  useTheme,
  Avatar,
  Stack,
} from '@mui/material';
import {
  PlayArrow as ApurarIcon,
  CheckCircle as AprovarIcon,
  Cancel as CancelarIcon,
  Receipt as LancarIcon,
  Refresh as RefreshIcon,
  AttachMoney,
  TrendingUp,
  HourglassEmpty,
  CheckCircleOutline,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import comissaoService from '../services/comissaoService';
import { IComissao } from '../types';

const ComissoesPage: React.FC = () => {
  const theme = useTheme();

  const mesAtual = new Date().toISOString().slice(0, 7);
  const [mesReferencia, setMesReferencia] = useState(mesAtual);
  const [comissoes, setComissoes] = useState<IComissao[]>([]);
  const [loading, setLoading] = useState(false);
  const [apurando, setApurando] = useState(false);
  const [selecionados, setSelecionados] = useState<number[]>([]);
  const [filtroStatus, setFiltroStatus] = useState<string>('');
  const [filtroTipo, setFiltroTipo] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({
    total: 0, pendentes: 0, aprovadas: 0, pagas: 0, canceladas: 0,
    valor_pendentes: 0, valor_aprovadas: 0, valor_pagas: 0,
  });

  const [dialogLancar, setDialogLancar] = useState(false);
  const [dataVencimento, setDataVencimento] = useState('');
  const [lancando, setLancando] = useState(false);

  const carregarComissoes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await comissaoService.getComissoes({
        mes_referencia: mesReferencia,
        status: filtroStatus || undefined,
        tipo: filtroTipo || undefined,
        page,
        limit: 50,
        sortBy: 'createdAt',
        sortOrder: 'DESC',
      });
      if (response.success) {
        setComissoes(response.data || []);
        setTotalPages((response as any).pagination?.pages || 1);
      }
    } catch (err) {
      toast.error('Erro ao carregar comissões');
    } finally {
      setLoading(false);
    }
  }, [mesReferencia, filtroStatus, filtroTipo, page]);

  const carregarEstatisticas = useCallback(async () => {
    try {
      const response = await comissaoService.getEstatisticas(mesReferencia);
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  }, [mesReferencia]);

  useEffect(() => {
    carregarComissoes();
    carregarEstatisticas();
  }, [carregarComissoes, carregarEstatisticas]);

  const handleApurar = async () => {
    const result = await Swal.fire({
      title: 'Apurar Comissões',
      text: `Deseja apurar todas as comissões do mês ${mesReferencia.split('-').reverse().join('/')}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sim, Apurar',
      cancelButtonText: 'Cancelar',
    });

    if (!result.isConfirmed) return;

    setApurando(true);
    try {
      const response = await comissaoService.apurarComissoes(mesReferencia);
      if (response.success) {
        toast.success(response.message || 'Apuração concluída');
        carregarComissoes();
        carregarEstatisticas();
      } else {
        toast.error(response.message || 'Erro na apuração');
      }
    } catch (err) {
      toast.error('Erro ao apurar comissões');
    } finally {
      setApurando(false);
    }
  };

  const handleAprovar = async () => {
    const pendentes = selecionados.filter(id =>
      comissoes.find(c => c.id === id && c.status === 'pendente')
    );

    if (pendentes.length === 0) {
      toast.warning('Selecione comissões pendentes para aprovar');
      return;
    }

    try {
      const response = await comissaoService.aprovarComissoes(pendentes);
      if (response.success) {
        toast.success(`${response.data?.aprovadas || pendentes.length} comissão(ões) aprovada(s)`);
        setSelecionados([]);
        carregarComissoes();
        carregarEstatisticas();
      }
    } catch (err) {
      toast.error('Erro ao aprovar comissões');
    }
  };

  const handleCancelar = async (id: number) => {
    const result = await Swal.fire({
      title: 'Cancelar Comissão',
      text: 'Deseja realmente cancelar esta comissão?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, Cancelar',
      cancelButtonText: 'Voltar',
      input: 'text',
      inputLabel: 'Motivo (opcional)',
    });

    if (!result.isConfirmed) return;

    try {
      const response = await comissaoService.cancelarComissao(id, result.value);
      if (response.success) {
        toast.success('Comissão cancelada');
        carregarComissoes();
        carregarEstatisticas();
      }
    } catch (err) {
      toast.error('Erro ao cancelar comissão');
    }
  };

  const handleAbrirLancar = () => {
    const aprovados = selecionados.filter(id =>
      comissoes.find(c => c.id === id && c.status === 'aprovada')
    );

    if (aprovados.length === 0) {
      toast.warning('Selecione comissões aprovadas para lançar em Contas a Pagar');
      return;
    }

    const hoje = new Date();
    hoje.setMonth(hoje.getMonth() + 1);
    hoje.setDate(10);
    setDataVencimento(hoje.toISOString().split('T')[0]);
    setDialogLancar(true);
  };

  const handleLancar = async () => {
    const aprovados = selecionados.filter(id =>
      comissoes.find(c => c.id === id && c.status === 'aprovada')
    );

    if (!dataVencimento) {
      toast.warning('Informe a data de vencimento');
      return;
    }

    setLancando(true);
    try {
      const response = await comissaoService.lancarDespesas(aprovados, dataVencimento);
      if (response.success) {
        const dados = response.data || [];
        const nomes = dados.map((d: any) => `${d.funcionario_nome}: ${comissaoService.formatCurrency(d.valor)}`).join('\n');

        await Swal.fire({
          title: 'Despesas Criadas!',
          html: `<p>${dados.length} despesa(s) criada(s) em Contas a Pagar:</p><pre style="text-align:left;font-size:13px">${nomes}</pre>`,
          icon: 'success',
        });

        setSelecionados([]);
        setDialogLancar(false);
        carregarComissoes();
        carregarEstatisticas();
      }
    } catch (err) {
      toast.error('Erro ao lançar despesas');
    } finally {
      setLancando(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelecionados(comissoes.filter(c => c.status !== 'paga' && c.status !== 'cancelada').map(c => c.id));
    } else {
      setSelecionados([]);
    }
  };

  const handleSelectItem = (id: number) => {
    setSelecionados(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const totalSelecionado = selecionados.reduce((acc, id) => {
    const c = comissoes.find(com => com.id === id);
    return acc + (c ? Number(c.valor_comissao) : 0);
  }, 0);

  const [mesAno] = mesReferencia.split('-').reverse();

  return (
    <Box sx={{ p: 0.5 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 1,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.04)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, width: 40, height: 40 }}>
              <AttachMoney sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Gestão de Comissões
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Gerenciamento de comissões de vendedores
              </Typography>
            </Box>
          </Box>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
            <TextField
              type="month"
              value={mesReferencia}
              onChange={(e) => { setMesReferencia(e.target.value); setPage(1); setSelecionados([]); }}
              size="small"
              sx={{ width: 180 }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={apurando ? <CircularProgress size={18} color="inherit" /> : <ApurarIcon />}
              onClick={handleApurar}
              disabled={apurando}
            >
              Apurar Mês
            </Button>
            <Tooltip title="Atualizar">
              <IconButton size="small" onClick={() => { carregarComissoes(); carregarEstatisticas(); }}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
      </Paper>

      {/* Cards de Estatísticas */}
      <Grid container spacing={1} mb={1}>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1 }}>
              <HourglassEmpty sx={{ fontSize: 40, color: theme.palette.warning.main }} />
              <Box>
                <Typography variant="body2" color="text.secondary">Pendentes</Typography>
                <Typography variant="h6" fontWeight="bold">{comissaoService.formatCurrency(stats.valor_pendentes)}</Typography>
                <Typography variant="caption" color="text.secondary">{stats.pendentes} registro(s)</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1 }}>
              <CheckCircleOutline sx={{ fontSize: 40, color: theme.palette.info.main }} />
              <Box>
                <Typography variant="body2" color="text.secondary">Aprovadas</Typography>
                <Typography variant="h6" fontWeight="bold">{comissaoService.formatCurrency(stats.valor_aprovadas)}</Typography>
                <Typography variant="caption" color="text.secondary">{stats.aprovadas} registro(s)</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1 }}>
              <AttachMoney sx={{ fontSize: 40, color: theme.palette.success.main }} />
              <Box>
                <Typography variant="body2" color="text.secondary">Pagas</Typography>
                <Typography variant="h6" fontWeight="bold">{comissaoService.formatCurrency(stats.valor_pagas)}</Typography>
                <Typography variant="caption" color="text.secondary">{stats.pagas} registro(s)</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <Card>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1 }}>
              <TrendingUp sx={{ fontSize: 40, color: theme.palette.primary.main }} />
              <Box>
                <Typography variant="body2" color="text.secondary">Total Geral</Typography>
                <Typography variant="h6" fontWeight="bold">{comissaoService.formatCurrency(stats.valor_pendentes + stats.valor_aprovadas + stats.valor_pagas)}</Typography>
                <Typography variant="caption" color="text.secondary">{stats.total} registro(s)</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtros e Ações */}
      <Paper sx={{ p: 1, mb: 1 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select value={filtroStatus} onChange={(e) => { setFiltroStatus(e.target.value); setPage(1); }} label="Status">
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="pendente">Pendente</MenuItem>
              <MenuItem value="aprovada">Aprovada</MenuItem>
              <MenuItem value="paga">Paga</MenuItem>
              <MenuItem value="cancelada">Cancelada</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Tipo</InputLabel>
            <Select value={filtroTipo} onChange={(e) => { setFiltroTipo(e.target.value); setPage(1); }} label="Tipo">
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="venda">Venda</MenuItem>
              <MenuItem value="recebimento">Recebimento</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ flexGrow: 1 }} />

          {selecionados.length > 0 && (
            <Alert severity="info" sx={{ py: 0 }}>
              {selecionados.length} selecionado(s) — Total: {comissaoService.formatCurrency(totalSelecionado)}
            </Alert>
          )}

          <Button
            variant="outlined"
            color="info"
            startIcon={<AprovarIcon />}
            onClick={handleAprovar}
            disabled={selecionados.length === 0}
          >
            Aprovar
          </Button>

          <Button
            variant="contained"
            color="success"
            startIcon={<LancarIcon />}
            onClick={handleAbrirLancar}
            disabled={selecionados.length === 0}
          >
            Lançar em Contas a Pagar
          </Button>
        </Box>
      </Paper>

      {/* Tabela de Comissões */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.08) }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selecionados.length > 0 && selecionados.length === comissoes.filter(c => c.status !== 'paga' && c.status !== 'cancelada').length}
                  indeterminate={selecionados.length > 0 && selecionados.length < comissoes.filter(c => c.status !== 'paga' && c.status !== 'cancelada').length}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </TableCell>
              <TableCell>Vendedor</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Contrato/Fatura</TableCell>
              <TableCell align="right">Valor Base</TableCell>
              <TableCell align="center">Cálculo</TableCell>
              <TableCell align="right">Comissão</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : comissoes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    Nenhuma comissão encontrada. Clique em "Apurar Mês" para gerar.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              comissoes.map((c) => (
                <TableRow key={c.id} hover selected={selecionados.includes(c.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selecionados.includes(c.id)}
                      onChange={() => handleSelectItem(c.id)}
                      disabled={c.status === 'paga' || c.status === 'cancelada'}
                    />
                  </TableCell>
                  <TableCell>{c.funcionario?.nome || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={comissaoService.getTipoLabel(c.tipo)}
                      size="small"
                      color={c.tipo === 'venda' ? 'primary' : 'secondary'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{c.cliente?.nome || '-'}</TableCell>
                  <TableCell>
                    {c.tipo === 'venda'
                      ? `Contrato #${c.contrato_id || '-'}`
                      : `Fatura #${c.fatura_id || '-'}`
                    }
                  </TableCell>
                  <TableCell align="right">{comissaoService.formatCurrency(c.valor_base)}</TableCell>
                  <TableCell align="center">
                    {c.calculo_tipo === 'percentual'
                      ? `${c.percentual_ou_fixo}%`
                      : comissaoService.formatCurrency(c.percentual_ou_fixo)
                    }
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {comissaoService.formatCurrency(c.valor_comissao)}
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={comissaoService.getStatusLabel(c.status)}
                      size="small"
                      color={comissaoService.getStatusColor(c.status)}
                    />
                  </TableCell>
                  <TableCell align="center">
                    {(c.status === 'pendente' || c.status === 'aprovada') && (
                      <Tooltip title="Cancelar">
                        <IconButton size="small" color="error" onClick={() => handleCancelar(c.id)}>
                          <CancelarIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2} gap={1}>
          <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
          <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
            Página {page} de {totalPages}
          </Typography>
          <Button size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Próxima</Button>
        </Box>
      )}

      {/* Dialog para Lançar em Contas a Pagar */}
      <Dialog open={dialogLancar} onClose={() => setDialogLancar(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Lançar em Contas a Pagar</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              {selecionados.filter(id => comissoes.find(c => c.id === id && c.status === 'aprovada')).length} comissão(ões) aprovada(s) selecionada(s).
              Será criada uma despesa por vendedor com o total das comissões.
            </Alert>
            <TextField
              label="Data de Vencimento *"
              type="date"
              value={dataVencimento}
              onChange={(e) => setDataVencimento(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Data de vencimento da despesa em Contas a Pagar"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogLancar(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleLancar}
            disabled={lancando || !dataVencimento}
            startIcon={lancando ? <CircularProgress size={18} color="inherit" /> : <LancarIcon />}
          >
            {lancando ? 'Lançando...' : 'Confirmar Lançamento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ComissoesPage;
